import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NavBar as SharedNavBar, navBarStyles } from '@iziping/ui';
import { useTournamentStore } from '../../../service/useTournamentStore';
import LogoImage from '../../../assets/iziping_logo_250px.png';
import Modal from '../../modal/Modal';
import UserSettingsPage from '../../../features/user/UserSettingsPage';

const NavBar: React.FC = () => {
    const { t } = useTranslation();
    const { selectedTournament } = useTournamentStore();
    const navigate = useNavigate();
    const [showSettings, setShowSettings] = useState(false);

    const { pathname } = useLocation();
    const navClass = ({ isActive }: { isActive: boolean }) =>
        `${navBarStyles.menuA}${isActive ? ` ${navBarStyles.menuActive}` : ''}`;
    const parentActive = (prefix: string) =>
        pathname.startsWith(prefix) ? `${navBarStyles.menuA} ${navBarStyles.menuActive}` : navBarStyles.menuA;

    return (
        <>
            <SharedNavBar logo={LogoImage} onSettingsClick={() => setShowSettings(true)} version={__APP_VERSION__}>
                <li>
                    <NavLink className={navClass} to="/" end>
                        {t('MENU_HOME')}
                    </NavLink>
                </li>
                <li className={navBarStyles.menuDropdown}>
                    <span className={parentActive('/tournaments')}>{t('MENU_TOURNAMENTS')}</span>
                    <ul className={navBarStyles.menuSubmenu}>
                        <li>
                            <NavLink className={navClass} to="/tournaments/new">
                                {t('MENU_TOURNAMENTS_NEW')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink className={navClass} to="/" end>
                                {t('MENU_TOURNAMENTS_LIST')}
                            </NavLink>
                        </li>
                    </ul>
                </li>
                <li className={navBarStyles.menuDropdown}>
                    <span className={parentActive('/players')}>{t('MENU_PLAYERS')}</span>
                    <ul className={navBarStyles.menuSubmenu}>
                        <li>
                            <NavLink className={navClass} to="/players/new">
                                {t('MENU_PLAYERS_NEW')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink className={navClass} to="/players" end>
                                {t('MENU_PLAYERS_LIST')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink className={navClass} to="/players/search">
                                {t('MENU_PLAYERS_SEARCH')}
                            </NavLink>
                        </li>
                    </ul>
                </li>
                <li className={navBarStyles.menuDropdown}>
                    <span className={parentActive('/clubs')}>{t('MENU_CLUBS')}</span>
                    <ul className={navBarStyles.menuSubmenu}>
                        <li>
                            <NavLink className={navClass} to="/clubs/new">
                                {t('MENU_CLUBS_NEW')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink className={navClass} to="/clubs" end>
                                {t('MENU_CLUBS_LIST')}
                            </NavLink>
                        </li>
                    </ul>
                </li>
                {selectedTournament && (
                    <li className={navBarStyles.menuDropdown}>
                        <span className={parentActive('/heats')}>{t('MENU_HEATS')}</span>
                        <ul className={navBarStyles.menuSubmenu}>
                            <li>
                                <NavLink className={navClass} to="/heats/new">
                                    {t('MENU_HEATS_NEW')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink className={navClass} to={`/tournaments/${selectedTournament.id}`}>
                                    {t('MENU_HEATS_LIST')}
                                </NavLink>
                            </li>
                        </ul>
                    </li>
                )}
                {selectedTournament && (
                    <li className={navBarStyles.menuDropdown}>
                        <span className={parentActive('/matches')}>{t('MENU_MATCHES')}</span>
                        <ul className={navBarStyles.menuSubmenu}>
                            <li>
                                <NavLink className={navClass} to="/matches/manage">
                                    {t('MENU_MATCHES_MANAGE')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink className={navClass} to="/matches/bracket">
                                    {t('MENU_MATCHES_BRACKET')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink className={navClass} to="/matches/finished">
                                    {t('MENU_MATCHES_HISTORY')}
                                </NavLink>
                            </li>
                        </ul>
                    </li>
                )}
                {selectedTournament && (
                    <li className={navBarStyles.menuDropdown}>
                        <span className={parentActive('/statistics')}>{t('MENU_STATISTICS')}</span>
                        <ul className={navBarStyles.menuSubmenu}>
                            <li>
                                <NavLink className={navClass} to="/statistics/results">
                                    {t('MENU_STATISTICS_RESULTS')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink className={navClass} to="/statistics/detail">
                                    {t('MENU_STATISTICS_DETAILS')}
                                </NavLink>
                            </li>
                            <li>
                                <NavLink className={navClass} to="/statistics/tables">
                                    {t('MENU_STATISTICS_TABLES')}
                                </NavLink>
                            </li>
                        </ul>
                    </li>
                )}
                {selectedTournament && (
                    <li
                        className={navBarStyles.menuA}
                        style={{ fontWeight: 'bold' }}
                        onClick={() => navigate(`/tournaments/${selectedTournament.id}`)}
                    >
                        {selectedTournament.name}
                    </li>
                )}
            </SharedNavBar>

            {showSettings && (
                <Modal onClose={() => setShowSettings(false)} className="modal-settings">
                    <UserSettingsPage onClose={() => setShowSettings(false)} />
                </Modal>
            )}
        </>
    );
};

export default NavBar;
